import React, { useState, useEffect, useContext, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { GoogleMap, MarkerClusterer, InfoWindowF } from '@react-google-maps/api'
import SearchLocationContext from '../../../context/SearchLocationContext'
import { useSearchLocation } from '../../../hooks/useSearchLocation'
import FittingCenter from '../../../assets/media/icons/fitting-center.svg'
import FittingCenterActive from '../../../assets/media/icons/fitting-center-active.svg'
import ClusterIcon from '../../../assets/media/icons/cluster.svg'
import ApplicationContext from '../../../context/ApplicationContext'
import { useWindowSize } from '../../../hooks/useWindowSize'
import InfoWindowBody from '../InfoWindowBody/InfoWindowBody'

const mapContainerStyle = {
    width: '100%',
    height: '100%',
}

// Map styles are now controlled via Google Cloud Console when using Map ID
// Custom styling should be configured at: https://console.cloud.google.com/google/maps-apis/studio/maps

// Get Map ID - check environment variable or use hardcoded fallback
const mapId = process.env.REACT_APP_GOOGLE_MAP_ID || ''

const defaultOptions = {
    minZoom: 3,
    mapId: mapId, // Set Map ID from the beginning
    // Don't include styles when Map ID is present - they're controlled by Cloud Console
    restriction: {
        latLngBounds: {
            north: 85,
            south: -85,
            west: -180,
            east: 180,
        },
    },
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
}

// Custom AdvancedMarkerElement component for React integration
const AdvancedMarker = ({ position, icon, onClick, map, label, zIndex, onError, ariaLabel, markerIndex, totalMarkers }) => {
    const markerRef = useRef(null)

    useEffect(() => {
        if (!map) return

        try {
            // Check if AdvancedMarkerElement is available
            if (!window.google?.maps?.marker?.AdvancedMarkerElement) {
                if (onError) onError(new Error('AdvancedMarkerElement not available'))
                return
            }

            // Create marker content
            const content = document.createElement('div')
            content.style.position = 'relative'
            content.style.display = 'flex'
            content.style.alignItems = 'center'
            content.style.justifyContent = 'center'
            content.style.cursor = 'pointer'
            content.style.transition = 'transform 0.2s, filter 0.2s'
            content.setAttribute('role', 'button')
            content.setAttribute('tabindex', markerIndex === 0 ? '0' : '-1')
            content.setAttribute('data-marker-index', markerIndex.toString())
            if (ariaLabel) {
                content.setAttribute('aria-label', `${ariaLabel} (${markerIndex + 1} of ${totalMarkers})`)
            }

            // Add focus styling
            content.addEventListener('focus', () => {
                content.style.transform = 'scale(1.2)'
                content.style.filter = 'drop-shadow(0 0 8px rgba(0, 123, 255, 0.8))'
            })
            content.addEventListener('blur', () => {
                content.style.transform = 'scale(1)'
                content.style.filter = 'none'
            })

            const img = document.createElement('img')
            img.src = icon
            img.style.width = '32px'
            img.style.height = '32px'
            content.appendChild(img)

            if (label) {
                const labelDiv = document.createElement('div')
                labelDiv.textContent = label.text
                labelDiv.style.position = 'absolute'
                labelDiv.style.color = label.color || '#fff'
                labelDiv.style.fontSize = label.fontSize || '12px'
                labelDiv.style.fontWeight = 'bold'
                labelDiv.style.textAlign = 'center'
                labelDiv.style.width = '100%'
                labelDiv.style.pointerEvents = 'none'
                content.appendChild(labelDiv)
            }

            // Create the advanced marker
            const advancedMarker = new window.google.maps.marker.AdvancedMarkerElement({
                map,
                position,
                content,
                zIndex: zIndex || 0,
            })

            // Add click and keyboard listeners
            if (onClick) {
                const handleActivate = event => {
                    event.preventDefault()
                    event.stopPropagation()
                    onClick(event)
                }

                content.addEventListener('click', handleActivate)
                content.addEventListener('keydown', event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleActivate(event)
                    }

                    // Arrow key navigation between markers
                    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                        event.preventDefault()
                        if (markerIndex < totalMarkers - 1) {
                            const nextMarker = document.querySelector(`[data-marker-index="${markerIndex + 1}"]`)
                            if (nextMarker) {
                                // Update tabindex for roving tabindex pattern
                                content.setAttribute('tabindex', '-1')
                                nextMarker.setAttribute('tabindex', '0')
                                nextMarker.focus()
                            }
                        }
                    }

                    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                        event.preventDefault()
                        if (markerIndex > 0) {
                            const prevMarker = document.querySelector(`[data-marker-index="${markerIndex - 1}"]`)
                            if (prevMarker) {
                                // Update tabindex for roving tabindex pattern
                                content.setAttribute('tabindex', '-1')
                                prevMarker.setAttribute('tabindex', '0')
                                prevMarker.focus()
                            }
                        }
                    }
                })
            }

            markerRef.current = advancedMarker

            // Cleanup
            return () => {
                if (markerRef.current) {
                    markerRef.current.map = null
                    if (content && onClick) {
                        content.removeEventListener('click', onClick)
                    }
                }
            }
        } catch (error) {
            console.error('Error creating AdvancedMarkerElement:', error)
            if (onError) onError(error)
        }
    }, [map, position, icon, onClick, label, zIndex, onError, ariaLabel, markerIndex, totalMarkers])

    // Update position if it changes
    useEffect(() => {
        if (markerRef.current && position) {
            try {
                markerRef.current.position = position
            } catch (error) {
                console.error('Error updating marker position:', error)
            }
        }
    }, [position])

    // Update content if icon or label changes
    useEffect(() => {
        if (markerRef.current && markerRef.current.content) {
            try {
                const content = markerRef.current.content
                const img = content.querySelector('img')
                if (img && icon) {
                    img.src = icon
                }

                const labelDiv = content.querySelector('div')
                if (labelDiv && label) {
                    labelDiv.textContent = label.text
                    labelDiv.style.color = label.color || '#fff'
                    labelDiv.style.fontSize = label.fontSize || '12px'
                }
            } catch (error) {
                console.error('Error updating marker content:', error)
            }
        }
    }, [icon, label])

    return null
}

AdvancedMarker.propTypes = {
    position: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
    }),
    icon: PropTypes.string,
    onClick: PropTypes.func,
    map: PropTypes.object,
    label: PropTypes.shape({
        text: PropTypes.string,
        color: PropTypes.string,
        fontSize: PropTypes.string,
    }),
    zIndex: PropTypes.number,
    onError: PropTypes.func,
    ariaLabel: PropTypes.string,
    markerIndex: PropTypes.number,
    totalMarkers: PropTypes.number,
}

const Map = () => {
    const [searchLocationData, setSearchLocationData] = useContext(SearchLocationContext)
    const [applicationData, setApplicationData] = useContext(ApplicationContext)
    const [localizations] = useState(applicationData.localizations)
    const { handleMapCenterUpdate, stableLocationList } = useSearchLocation()
    const [mapCenter, setMapCenter] = useState(searchLocationData.mapCenter)
    const [mapZoom, setMapZoom] = useState(searchLocationData.defaultZoom)
    // eslint-disable-next-line no-unused-vars
    const [sortBy, setSortBy] = useState(searchLocationData.sortBy)
    // eslint-disable-next-line no-unused-vars
    const [appliedFilters, setAppliedFilters] = useState(searchLocationData.appliedFilters)
    const locationList = stableLocationList
    const [highlightedId, setHighlightedId] = useState()
    const { isMobile } = useWindowSize()
    const mapRef = useRef()
    const [mapInstance, setMapInstance] = useState(null)
    const idleTimeoutRef = useRef(null)
    const lastUpdatePositionRef = useRef({ lat: null, lng: null, zoom: null })

    // Helper function to calculate distance between two points in kilometers
    const calculateDistance = (lat1, lng1, lat2, lng2) => {
        const R = 6371 // Earth's radius in kilometers
        const dLat = (lat2 - lat1) * Math.PI / 180
        const dLng = (lng2 - lng1) * Math.PI / 180
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLng / 2) * Math.sin(dLng / 2)
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
        return R * c
    }

    // Check if the map movement is significant enough to trigger a new search
    const isSignificantMovement = (newLat, newLng, newZoom) => {
        const lastPos = lastUpdatePositionRef.current

        // Always update on first load
        if (lastPos.lat === null || lastPos.lng === null) {
            return true
        }

        // Check zoom level change
        if (Math.abs(newZoom - lastPos.zoom) >= 1) {
            return true
        }

        // Check distance moved - require at least 1km movement for new search
        const distance = calculateDistance(lastPos.lat, lastPos.lng, newLat, newLng)
        return distance >= 1 // 1 kilometer threshold
    }

    const onMapLoad = useCallback(map => {
        mapRef.current = map
        setMapInstance(map)

        // Just import the marker library, Map ID is already set in options
        setTimeout(async () => {
            try {
                await window.google.maps.importLibrary('marker')
                console.log('AdvancedMarkerElement ready with Map ID:', mapId)
            } catch (error) {
                console.warn('Error importing marker library:', error)
            }
        }, 100)
    }, [])

    const onIdle = () => {
        if (!mapRef.current) return

        // Clear any existing timeout
        if (idleTimeoutRef.current) {
            clearTimeout(idleTimeoutRef.current)
        }

        // Debounce the idle event by 500ms
        idleTimeoutRef.current = setTimeout(() => {
            if (!mapRef.current) return

            const center = mapRef.current.getCenter()
            const zoom = mapRef.current.getZoom()
            const newLat = center.lat()
            const newLng = center.lng()

            // Only trigger update if movement is significant
            if (isSignificantMovement(newLat, newLng, zoom)) {
                lastUpdatePositionRef.current = { lat: newLat, lng: newLng, zoom }

                handleMapCenterUpdate({
                    lat: newLat,
                    lng: newLng,
                    zoom,
                    disableLoading: Boolean(searchLocationData.activeLocationId) || Boolean(searchLocationData.eventsListingActiveLocationId),
                })
            }
        }, 500) // 500ms debounce
    }

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (idleTimeoutRef.current) {
                clearTimeout(idleTimeoutRef.current)
            }
        }
    }, [])

    useEffect(() => {
        setMapCenter(searchLocationData.mapCenter)
        setMapZoom(searchLocationData.defaultZoom)
        setSortBy(searchLocationData.sortBy)
        setAppliedFilters(searchLocationData.appliedFilters)
        // locationList now comes from stableLocationList - no need to update from context
        // setActiveId(searchLocationData.activeLocationId) // removed unused variable
        setHighlightedId(searchLocationData.highlightedLocationId)
    }, [searchLocationData])

    const renderMarkers = () =>
        mapInstance &&
        locationList.map((location, index) => (
            <AdvancedMarker
                key={location.locationId}
                map={mapInstance}
                position={{ lat: location.latitude, lng: location.longitude }}
                icon={location.locationId === highlightedId ? FittingCenterActive : FittingCenter}
                ariaLabel={location.name || `Location ${location.locationId}`}
                markerIndex={index}
                totalMarkers={locationList.length}
                onClick={() => {
                    // Always update the highlighted/active state when marker is clicked
                    setSearchLocationData(prevState => ({
                        ...prevState,
                        activeLocationId: isMobile ? null : location.locationId,
                        highlightedLocationId: location.locationId, // Always show InfoWindow on marker click
                    }))
                    setApplicationData(prevState => {
                        return {
                            ...prevState,
                            overlay: true,
                        }
                    })
                }}
                zIndex={location.locationId === highlightedId ? 1000 : 1}
                onError={error => {
                    console.error('AdvancedMarker failed for location:', location.locationId, error)
                }}
            />
        ))

    const renderClusterer = clusterer =>
        mapInstance &&
        clusterer.markers.map(cluster => (
            <AdvancedMarker
                key={cluster.id}
                map={mapInstance}
                position={cluster.position}
                icon={ClusterIcon}
                label={{
                    text: String(cluster.count),
                    color: 'var(--common-color-light)',
                    fontSize: 'var(--cluster-text-size)',
                }}
                onClick={() => {
                    const lat = cluster.position.lat()
                    const lng = cluster.position.lng()
                    setSearchLocationData(prevState => ({
                        ...prevState,
                        activeLocationId: null,
                        highlightedLocationId: null,
                        mapCenter: { lat, lng },
                        defaultZoom: cluster.count >= 10 ? mapZoom + 2 : mapZoom + 1,
                    }))
                }}
                zIndex={100}
                onError={error => {
                    console.error('AdvancedMarker failed for cluster:', cluster.id, error)
                }}
            />
        ))

    // Disabled - activeInfoWindow is not a native InfoWindow object
    // useEffect(() => {
    //     if (!highlightedId && activeInfoWindow) {
    //         activeInfoWindow.close()
    //     }
    // }, [highlightedId, activeInfoWindow])

    const onDetailsClick = id => {
        setSearchLocationData(prevState => {
            return {
                ...prevState,
                activeLocationId: id,
            }
        })
        setApplicationData(prevState => {
            return {
                ...prevState,
                overlay: true,
            }
        })
    }

    return (
        <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={mapZoom}
            options={defaultOptions}
            onLoad={onMapLoad}
            onIdle={onIdle}
        >
            <MarkerClusterer>{clusterer => renderClusterer(clusterer)}</MarkerClusterer>
            {renderMarkers()}
            {highlightedId && locationList.find(l => l.locationId === highlightedId) && (
                <InfoWindowF
                    key={`infowindow-${highlightedId}`}
                    autoPan={false}
                    options={{
                        pixelOffset: new window.google.maps.Size(0, -50),
                    }}
                    position={{
                        lat: locationList.find(l => l.locationId === highlightedId)?.latitude,
                        lng: locationList.find(l => l.locationId === highlightedId)?.longitude,
                    }}
                    onCloseClick={() => {
                        setHighlightedId(null)
                        setSearchLocationData(prevState => {
                            return {
                                ...prevState,
                                ...prevState.prevMapState, // Restore previous zoom, filters, etc.
                                activeLocationId: null,
                                highlightedLocationId: null,
                            }
                        })
                        setApplicationData(prevState => {
                            return {
                                ...prevState,
                                overlay: false,
                            }
                        })
                    }}
                >
                    <InfoWindowBody
                        onDetailsClick={onDetailsClick}
                        location={locationList.find(l => l.locationId === highlightedId)}
                        localizations={localizations}
                    />
                </InfoWindowF>
            )}
        </GoogleMap>
    )
}

export default Map
